import React from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { StatCard } from '../components/ui/StatCard';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Table } from '../components/ui/Table';
import { InvoiceStatusBadge, SubscriptionStatusBadge } from '../components/domain/StatusBadge';
import { formatCurrency, formatDate, isOverdue, getCycleName } from '../utils/calculations';
import { Invoice, Subscription } from '../types';
import { DollarSignIcon, FileTextIcon, RepeatIcon, AlertTriangleIcon, PlusIcon } from 'lucide-react';
export function Dashboard() {
  const {
    filteredInvoices,
    filteredSubscriptions,
    getCustomerById,
    selectedCompany
  } = useData();
  // Calculate stats
  const totalRevenue = filteredInvoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.total_amount, 0);
  const unpaidInvoices = filteredInvoices.filter(inv => inv.status === 'sent').length;
  const activeSubscriptions = filteredSubscriptions.filter(sub => sub.status === 'active').length;
  const overdueSubscriptions = filteredSubscriptions.filter(sub => sub.status === 'active' && isOverdue(sub.next_billing_date)).length;
  // Recent invoices (last 8)
  const recentInvoices = [...filteredInvoices].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 8);
  // Upcoming subscriptions (next 5 active)
  const upcomingSubscriptions = [...filteredSubscriptions].filter(sub => sub.status === 'active').sort((a, b) => new Date(a.next_billing_date).getTime() - new Date(b.next_billing_date).getTime()).slice(0, 5);
  const invoiceColumns = [{
    key: 'id',
    header: 'Invoice',
    render: (invoice: Invoice) => <Link to={`/invoices/${invoice.id}`} className="text-blue-600 hover:text-blue-800 font-medium" data-id="element-271">
          {invoice.id.slice(0, 12)}...
        </Link>
  }, {
    key: 'customer',
    header: 'Customer',
    render: (invoice: Invoice) => {
      const customer = getCustomerById(invoice.customer_id);
      return customer?.name || 'Unknown';
    }
  }, {
    key: 'date',
    header: 'Date',
    render: (invoice: Invoice) => formatDate(invoice.date)
  }, {
    key: 'total',
    header: 'Total',
    render: (invoice: Invoice) => formatCurrency(invoice.total_amount)
  }, {
    key: 'status',
    header: 'Status',
    render: (invoice: Invoice) => <InvoiceStatusBadge status={invoice.status} data-id="element-272" />
  }];
  const subscriptionColumns = [{
    key: 'customer',
    header: 'Customer',
    render: (sub: Subscription) => {
      const customer = getCustomerById(sub.customer_id);
      return customer?.name || 'Unknown';
    }
  }, {
    key: 'cycle',
    header: 'Cycle',
    render: (sub: Subscription) => getCycleName(sub.cycle_days)
  }, {
    key: 'nextBilling',
    header: 'Next Billing',
    render: (sub: Subscription) => {
      const overdue = isOverdue(sub.next_billing_date);
      return <span className={overdue ? 'text-red-600 font-medium' : ''} data-id="element-273">
            {formatDate(sub.next_billing_date)}
            {overdue && ' (Overdue)'}
          </span>;
    }
  }, {
    key: 'status',
    header: 'Status',
    render: (sub: Subscription) => <SubscriptionStatusBadge status={sub.status} data-id="element-274" />
  }];
  return <div className="space-y-8" data-id="element-275">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-276">
        <div data-id="element-277">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-278">Dashboard</h1>
          <p className="text-gray-500 mt-1" data-id="element-279">
            Welcome back! Here's what's happening with {selectedCompany?.name}.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-280">
          <Link to="/invoices/new" data-id="element-281">
            <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-283" />} data-id="element-282">
              New Invoice
            </Button>
          </Link>
          <Link to="/subscriptions/new" data-id="element-284">
            <Button variant="secondary" leftIcon={<PlusIcon className="w-4 h-4" data-id="element-286" />} data-id="element-285">
              New Subscription
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-287">
        <StatCard title="Total Revenue" value={formatCurrency(totalRevenue)} icon={<DollarSignIcon className="w-6 h-6" data-id="element-289" />} data-id="element-288" />
        <StatCard title="Unpaid Invoices" value={unpaidInvoices} icon={<FileTextIcon className="w-6 h-6" data-id="element-291" />} data-id="element-290" />
        <StatCard title="Active Subscriptions" value={activeSubscriptions} icon={<RepeatIcon className="w-6 h-6" data-id="element-293" />} data-id="element-292" />
        <StatCard title="Overdue Bills" value={overdueSubscriptions} icon={<AlertTriangleIcon className="w-6 h-6" data-id="element-295" />} className={overdueSubscriptions > 0 ? 'border-red-200 bg-red-50' : ''} data-id="element-294" />
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-296">
        {/* Recent Invoices */}
        <div className="lg:col-span-2" data-id="element-297">
          <Card padding="none" data-id="element-298">
            <div className="p-6 border-b border-gray-200" data-id="element-299">
              <CardHeader title="Recent Invoices" subtitle="Your latest billing activity" action={<Link to="/invoices" data-id="element-301">
                    <Button variant="ghost" size="sm" data-id="element-302">
                      View All
                    </Button>
                  </Link>} data-id="element-300" />
            </div>
            <Table columns={invoiceColumns} data={recentInvoices} keyExtractor={inv => inv.id} emptyMessage="No invoices yet" data-id="element-303" />
          </Card>
        </div>

        {/* Upcoming Subscriptions */}
        <div data-id="element-304">
          <Card padding="none" data-id="element-305">
            <div className="p-6 border-b border-gray-200" data-id="element-306">
              <CardHeader title="Upcoming Renewals" subtitle="Next billing dates" action={<Link to="/subscriptions" data-id="element-308">
                    <Button variant="ghost" size="sm" data-id="element-309">
                      View All
                    </Button>
                  </Link>} data-id="element-307" />
            </div>
            <div className="divide-y divide-gray-200" data-id="element-310">
              {upcomingSubscriptions.length === 0 ? <div className="p-6 text-center text-gray-500" data-id="element-311">
                  No upcoming renewals
                </div> : upcomingSubscriptions.map(sub => {
              const customer = getCustomerById(sub.customer_id);
              const overdue = isOverdue(sub.next_billing_date);
              return <Link key={sub.id} to={`/subscriptions/${sub.id}`} className="block p-4 hover:bg-gray-50 transition-colors" data-id="element-312">
                      <div className="flex items-center justify-between" data-id="element-313">
                        <div data-id="element-314">
                          <p className="font-medium text-gray-900" data-id="element-315">
                            {customer?.name || 'Unknown'}
                          </p>
                          <p className="text-sm text-gray-500" data-id="element-316">
                            {getCycleName(sub.cycle_days)}
                          </p>
                        </div>
                        <div className="text-right" data-id="element-317">
                          <p className={`text-sm font-medium ${overdue ? 'text-red-600' : 'text-gray-900'}`} data-id="element-318">
                            {formatDate(sub.next_billing_date)}
                          </p>
                          {overdue && <p className="text-xs text-red-600" data-id="element-319">Overdue</p>}
                        </div>
                      </div>
                    </Link>;
            })}
            </div>
          </Card>
        </div>
      </div>
    </div>;
}